/* eslint-disable complexity */
import CheckedIndicator from '../CheckedIndicator/CheckedIndicator'
import Downshift from 'downshift'
import MultiselectClearButton from './MultiselectClearButton'
import NoSearchResultFound from './NoSearchResultFound'
import SearchInput from '../SearchInput/SearchInput'
import classes from './MultiselectDropdown.module.scss'
import natsort from 'natsort'
import {
  Color,
  Paragraph,
  ScrollIntoView,
  Tooltip
} from '../..'
import {
  useRef,
  useState
} from 'react'
import type { ButtonShape } from '@eggplant/types'
import type {
  CSSProperties,
  ReactNode,
  UIEventHandler
} from 'react'
import type {
  ControllerStateAndHelpers,
  DownshiftState,
  GetItemPropsOptions,
  StateChangeOptions
} from 'downshift'

export interface ButtonProps {
  getInputProps: () => void
  getItemProps: () => void
  getLabelProps: () => void
  getMenuProps: () => void
  getRootProps: () => void
  getToggleButtonProps: () => void
  isOpen: boolean
}

interface Option {
  label: string
  value: string
}

export interface Props {
  preferredPosition?: 'left' | 'right'
  hasClear?: boolean
  hasSearch?: boolean
  onChange: (value: string[]) => void
  optionListGroups: Option[]
  renderButton: (props: ButtonShape) => void
  selectedValueList: string[]
  onIsOpenStateChange?: (value: boolean) => void
  capacity?: number
  isOptionsSorted?: boolean
  noOptionsPlaceholder?: ReactNode
  hasDropdownWithNarrowGap?: boolean // true if the dropdown button smaller than normal button, such as iconButton or SmallButton
  renderOptionTooltip?: (currentOption: Option) => ReactNode
}

const {
  keyDownEnter,
  clickItem,
  clickButton
} = Downshift.stateChangeTypes

const itemToString = (option?: Option | null) => option
  ? option.value
  : ''

const DefaultNoOptionsPlaceholder = ({ children }: {children: ReactNode}) =>
  <div className={classes.NoSearchResult}>
    <div />
    <Paragraph>
      <Color variant='theme-description-text'>
        {children}
      </Color>
    </Paragraph>
  </div>

const MultiselectSingularDropdown = ({
  preferredPosition = 'left',
  hasClear = true,
  hasSearch = true,
  optionListGroups,
  onChange,
  renderButton,
  selectedValueList,
  onIsOpenStateChange,
  capacity,
  isOptionsSorted = true,
  noOptionsPlaceholder = 'No options found',
  hasDropdownWithNarrowGap = false,
  renderOptionTooltip
}: Props) => {
  const [input, setInput] = useState('')
  const [defaultOptionNumber, setDefaultOptionNumber] = useState(100)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const comboBoxRef = useRef<HTMLDivElement>(null)
  const sorter = natsort({ insensitive: true })

  const sortedOptions = isOptionsSorted
    // eslint-disable-next-line fp/no-mutating-methods
    ? optionListGroups.sort((current, next) =>
      sorter(current.label, next.label)
    )
    : optionListGroups

  const defaultSelectionsNumber = sortedOptions.slice(0, defaultOptionNumber)

  const filteredOptions = input
    ? sortedOptions.filter(option =>
      option.label.toLowerCase().includes(input.toLowerCase())
    ).slice(0, defaultOptionNumber)
    : defaultSelectionsNumber

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
  }

  window.addEventListener('resize', handleResize)

  const handleChange = (selectedItem: Option | null) => {
    if (!selectedItem) return

    if (selectedValueList.includes(selectedItem.value)) {
      onChange(selectedValueList.filter(item => item !== selectedItem.value))

      return
    }

    if (capacity !== undefined && selectedValueList.length === capacity) {
      onChange(selectedValueList)

      return
    }

    onChange(selectedValueList.concat(selectedItem.value.toString()))
  }

  const onInputValueChange = (inputValue: string | null, stateAndHelpers: ControllerStateAndHelpers<Option>) => {
    const searchInput = stateAndHelpers.selectedItem?.value === inputValue
      ? input
      : inputValue

    const optionsNumber = (stateAndHelpers.selectedItem?.value === inputValue) || inputValue === ''
      ? defaultOptionNumber
      : 100

    setDefaultOptionNumber(optionsNumber)
    setInput(searchInput || '')
  }

  const onOuterClick = () => {
    onChange(selectedValueList)
    setDefaultOptionNumber(100)
    setInput('')
    onIsOpenStateChange && onIsOpenStateChange(false)
  }

  const getComboBoxLeftPositionStyle = (comboBoxRef: React.RefObject<HTMLDivElement>): CSSProperties => {
    if (!comboBoxRef.current) {
      return preferredPosition === 'left'
        ? { left: '0px' }
        : { right: '0px' }
    }

    const comboxBoxPos = preferredPosition === 'left'
      ? comboBoxRef.current.getBoundingClientRect().left + 450
      : comboBoxRef.current.getBoundingClientRect().right + 450

    if (comboxBoxPos > windowWidth) {
      return preferredPosition === 'left'
        ? { left: `${windowWidth - comboxBoxPos}px` }
        : { right: `${windowWidth - comboxBoxPos}px` }
    }

    return preferredPosition === 'left'
      ? { left: '0px' }
      : { right: '0px' }
  }

  const handleScroll: UIEventHandler<HTMLDivElement> | undefined = event => {
    const {
      offsetHeight,
      scrollHeight,
      scrollTop
    } = event.target as HTMLDivElement

    // TODO: Allow a tolerance before hitting the bottom
    const isAtBottom = scrollTop + offsetHeight >= scrollHeight

    if (isAtBottom && defaultOptionNumber < 500) setDefaultOptionNumber(defaultOptionNumber + 100)
  }

  const renderOptions = (options: Option[], getItemProps: (options: GetItemPropsOptions<Option>) => any) => options.map(option => {
    const itemProps = getItemProps({
      item: option
    })

    const isChecked =
      !!selectedValueList.length &&
      selectedValueList.includes(option.value)

    // aria-selected is used by Downshift for basic highlighting, but we need it to show selections
    const className = itemProps['aria-selected']
      ? classes.highlighted
      : undefined

    return (
      <li
        {...itemProps}
        // When an item is actually chosen, not just highlighted
        aria-disabled={(capacity !== undefined && selectedValueList.length === capacity) && !isChecked}
        aria-selected={isChecked}
        className={className}
        key={option.value}
        role='option'
      >
        <Tooltip
          backgroundVariance={
            renderOptionTooltip
              ? 'white'
              : undefined
          }
          key={option.value}
          label={
            renderOptionTooltip
              ? renderOptionTooltip(option)
              : option.label
          }
        >
          <div className={classes.checkboxStyle}>
            <CheckedIndicator isChecked={isChecked} />
            <div className={classes.textStyle}>
              {option.label}
            </div>
          </div>
        </Tooltip>
      </li>
    )
  })

  const handleInputClear = () => { setInput('') }

  const handleClear = () => { onChange([]) }

  const maximumListDescription =
    <div className={classes.maximumListWrapperStyle}>
      <Paragraph>
        <Color variant='theme-placeholder-text'>
          {optionListGroups.length - 500} more requirements available,
          <br />
          please search to select
        </Color>
      </Paragraph>
    </div>

  // Prevent the default behavior of closing the menu upon item selection
  const stateReducer = (state: DownshiftState<Option>, changes: StateChangeOptions<Option>) => {
    // When change requirement group and clear all requirement, selectedList is not getting clear
    if (changes.type === clickButton && selectedValueList.length === 0) {
      handleClear()
    } else if (changes.type === clickButton) { // When click on requirement icon to close the dropdown, the selectedValueList is not updated
      onOuterClick()
    }

    if (comboBoxRef.current && changes.type === clickButton) {
      comboBoxRef.current.scrollIntoView()
    }

    if (changes.highlightedIndex == null && changes.selectedItem == null) {
      onIsOpenStateChange && onIsOpenStateChange(!!changes.isOpen)
    }

    const isOpeningChangeType =
    changes.type === keyDownEnter ||
    changes.type === clickItem

    return isOpeningChangeType
      ? {
        ...changes,
        highlightedIndex: state.highlightedIndex,
        isOpen: true
      }
      : changes
  }

  const capacityLabel = capacity !== undefined &&
    <div className={classes.externalQueryWrapperStyle}>
      <Paragraph>
        <Color variant='theme-placeholder-text'>
          {selectedValueList.length}/{capacity} added
        </Color>
      </Paragraph>
    </div>

  const hasExternalQuery = capacity !== undefined || selectedValueList.length > 0

  const capacityClearClassname = capacity !== undefined
    ? ' ' + classes.justifySpaceBetween
    : ''

  const clearSectionClassName = classes.clearSection + capacityClearClassname

  const renderClearSection = hasExternalQuery &&
    <div className={clearSectionClassName}>
      {capacityLabel}
      {selectedValueList.length > 0 && <MultiselectClearButton onClear={handleClear} />}
    </div>

  const preferpositionClassname = preferredPosition === 'left'
    ? classes.left
    : classes.right

  const narrowDropdownClassname = hasDropdownWithNarrowGap
    ? classes.narrowGap
    : ''

  const dropdownClass = [
    classes.dropdownWrapper,
    preferpositionClassname,
    narrowDropdownClassname
  ].filter(styleClassname => styleClassname).join(' ')

  return (
    <div ref={comboBoxRef}>
      <Downshift
        itemToString={itemToString}
        onChange={handleChange}
        onInputValueChange={onInputValueChange}
        onOuterClick={onOuterClick}
        selectedItem={null}
        stateReducer={stateReducer}
      >
        {
          ({
            getInputProps,
            getItemProps,
            getLabelProps,
            getMenuProps,
            getRootProps,
            getToggleButtonProps,
            isOpen
          }) =>
          // The span is not really a combobox since it does not contain a text input
            <span
              className={classes.MultiselecSingularDropdown}
              {...getRootProps(undefined, { suppressRefError: true })}
              role={undefined}
            >
              {
                renderButton({
                  ...getToggleButtonProps(),
                  ...getLabelProps(), // The button contains the labeling text
                  'aria-activedescendant': getInputProps()['aria-activedescendant']
                })
              }
              {
                isOpen &&
                  <div
                    className={dropdownClass}
                    style={getComboBoxLeftPositionStyle(comboBoxRef)}
                  >
                    {
                      sortedOptions.length
                        ? (
                          <>
                            {
                              hasSearch &&
                                <div className={classes.searchWrapper}>
                                  <SearchInput
                                    {
                                      ...getInputProps()
                                    }
                                    isAutoFocus
                                    onClear={handleInputClear}
                                    value={input}
                                  />
                                </div>
                            }
                            <ul
                              {...getMenuProps()}
                              // something using activedescendant must be focusable
                              aria-multiselectable
                              onScroll={handleScroll}
                              role='listbox'
                            >
                              {renderOptions(filteredOptions, getItemProps)}
                              {
                                defaultOptionNumber === 500 && optionListGroups.length > 500 && maximumListDescription
                              }
                            </ul>
                            {
                              !!sortedOptions.length && !filteredOptions.length &&
                                <NoSearchResultFound />
                            }
                            {
                              hasClear && renderClearSection
                            }
                            <ScrollIntoView />
                          </>
                        )
                        : (
                          <DefaultNoOptionsPlaceholder>
                            {noOptionsPlaceholder}
                          </DefaultNoOptionsPlaceholder>
                        )
                    }
                  </div>
              }
            </span>
        }
      </Downshift>
    </div>
  )
}

export default MultiselectSingularDropdown
